import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Grid,
} from '@mui/material';
import { Plan, Feature } from '../../../shared/adminFbacTypes';
import { getPlans, getFeatures, getPlanFeatures, updatePlanFeatures } from '../services/adminFbacApi';

const PlanMappingsPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [mappedFeatures, setMappedFeatures] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [plansData, featuresData] = await Promise.all([getPlans(), getFeatures()]);
      setPlans(plansData.filter(p => p.isActive));
      setFeatures(featuresData);
    } catch (err: any) {
      setError(err.message || 'Failed to load initial data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    const fetchMappings = async () => {
      if (!selectedPlanId) {
        setMappedFeatures(new Set());
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const mapped = await getPlanFeatures(selectedPlanId);
        setMappedFeatures(new Set(mapped));
      } catch (err: any) {
        setError(err.message || `Failed to fetch mappings for plan ${selectedPlanId}.`);
      } finally {
        setLoading(false);
      }
    };
    fetchMappings();
  }, [selectedPlanId]);

  const handleToggleFeature = (featureKey: string) => {
    setMappedFeatures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(featureKey)) {
        newSet.delete(featureKey);
      } else {
        newSet.add(featureKey);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!selectedPlanId) return;
    try {
      setLoading(true);
      setError(null);
      await updatePlanFeatures(selectedPlanId, Array.from(mappedFeatures));
      // Optionally show a success message
    } catch (err: any) {
      setError(err.message || 'Failed to save mappings.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Plan to Feature Mappings</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Select a Plan</Typography>
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select
                value={selectedPlanId}
                label="Plan"
                onChange={(e) => setSelectedPlanId(e.target.value)}
                disabled={loading}
              >
                {plans.map((plan) => (
                  <MenuItem key={plan.planId} value={plan.planId}>
                    {plan.friendlyName} ({plan.planId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, position: 'relative' }}>
            <Typography variant="h6" gutterBottom>Map Features</Typography>
            {loading && <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%' }} />}
            <List sx={{ maxHeight: 400, overflow: 'auto', opacity: loading ? 0.5 : 1 }}>
              {features.map((feature) => (
                <ListItem key={feature.featureKey} dense button onClick={() => handleToggleFeature(feature.featureKey)} disabled={!selectedPlanId || loading}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={mappedFeatures.has(feature.featureKey)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={feature.featureKey} secondary={feature.entitlementType} />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" onClick={handleSave} disabled={!selectedPlanId || loading}>
                Save Mappings
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlanMappingsPage;